/**
 * THE STATEMENT OF ADVICE FOR THE MODULES THAT HAD NONE (owner, 2 Oct 2026): retirement village,
 * downsizer, granny flat, and cashflow & pension.
 *
 * ONE DOCUMENT, FOUR MODULES. The aged care Statement of Advice is the format the owner wants kept
 * ("easy to read and follow a similar format"): a cover, a booklet read one section at a time, the
 * same stylesheet and the same navigation script. This file is that booklet with the aged-care-only
 * pages taken out and the module-specific page made a parameter. Each module hands over a
 * `ModuleSoaSnapshot` — plain data, frozen at generation, including the licensee attribution — and
 * `buildModuleSoaHtml` renders it. A snapshot is stored with the advice record and re-rendered later
 * by the licensee view, so nothing here may depend on who is signed in at render time.
 *
 * WHAT THE CORPORATIONS ACT ASKS OF A STATEMENT OF ADVICE (s947B for a licensee, s947C for an
 * authorised representative; RG 175 on clear, concise and effective), and where each lives here:
 *   - a statement setting out the advice ............................. "What we recommend"
 *   - the basis on which it is given: the client's objectives, financial
 *     situation and needs, and the information relied on ............ "Your situation today",
 *                                                                       "Why this advice fits you", appendix
 *   - a warning where that information may be incomplete or inaccurate
 *     (s961H) ........................................................ "Your situation today", "Risks & limitations"
 *   - the name and contact details of the providing entity and, for a
 *     representative, the authorising licensee ...................... cover, "Fees & disclosures"
 *   - remuneration, commissions and other benefits .................. "Fees & disclosures"
 *   - associations or relationships that might influence the advice . "Fees & disclosures"
 *   - where one financial product is replaced by another (s947D): the
 *     charges, the consequences and any benefits lost ............... "If this advice replaces one
 *                                                                       investment with another"
 *   - the risks of the recommendation (RG 175, best interests) ...... "What could go wrong", "Risks & limitations"
 *
 * THE LOOK AND THE SHARED PIECES (3 Oct 2026) live in soaKit.ts — the theme without pastel fills,
 * "Your situation today", the projection charts, the sortable comparison, the contents list and
 * the rates appendix — so the aged care and Support at Home documents draw on the same pieces.
 *
 * THE CONSUMER HAT. The reader is the client, not an adviser. So the executive summary says the
 * recommendation in one line and shows the three numbers that matter against staying put; every
 * part of the plan is named in everyday words with what it does; the risks are the same bold-claim-
 * plus-one-sentence list the card showed; and anything technical sits in the appendix.
 */
import { stockPhoto } from '../../assets/stock/index.ts';
import { formatCurrency } from '../../utils/index.ts';
import { ratesReviewStatus } from '../../../../shared/src/calculation/ratesReview.ts';
import { assetAssumptionFiguresFor } from '../../../../shared/src/calculation/assetAssumptionRows.ts';
import { buildDigitalSoaStructureAppendix, buildDigitalSoaStructureAppendixHtml } from '../../utils/digitalSoaStructureAppendix.ts';
import { buildFactFindSections, type SoaFactFindSection } from '../agedCare/AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import { periodOpenedOn } from '../agedCare/ProjectionsModal.tsx';
import { getCurrentAdviceAttribution } from '../professionalPortal/adviceAttribution.ts';
import { buildStrategyRisks, type RiskKind, type StrategyRisks, type StrategyRiskSpecifics } from '../../strategy/strategyRisks.ts';
import { buildAssumptionSections, type AssumptionSection } from './assumptionSections.ts';
import { SOA_THEME_CSS, situationFrom, renderSituationHtml, renderProjectionCharts, renderComparisonTable, renderToc, renderAssumptionsAppendixHtml, type SoaSituation, type SoaComparisonRow } from './soaKit.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { pensionCallout } from './agePensionClaim.ts';
import { renderGuaranteedIncomeSectionHtml, type SoaGuaranteedIncome } from './soaGuaranteedIncome.ts';
import { isDefinedBenefitPension } from '../../../../shared/src/calculation/definedBenefitPension.ts';

const soaBookletScriptUrl = new URL('../agedCare/soaBookletScript.ts', import.meta.url).href;

export type ModuleSoaKind = 'retirement_village' | 'downsizer' | 'granny_flat' | 'cashflow_pension';

export const MODULE_SOA_TITLES: Record<ModuleSoaKind, string> = {
    retirement_village: 'Your retirement village strategy',
    downsizer: 'Your downsizing strategy',
    granny_flat: 'Your granny flat strategy',
    cashflow_pension: 'Your cashflow and Age Pension strategy',
};

export const MODULE_SOA_LABELS: Record<ModuleSoaKind, string> = {
    retirement_village: 'Retirement village',
    downsizer: 'Downsizer',
    granny_flat: 'Granny flat',
    cashflow_pension: 'Cashflow & pension',
};

/* ─── Small helpers (the aged care file keeps its own private copies) ─────────────────────────── */

export function escapeHtml(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const text = (v: unknown): string => String(v ?? '').trim();
const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
const prettify = (key: string): string =>
    key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export function formatDateOnly(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** A card clause ("the pension does not move") as a sentence of its own. */
export const sentence = (clause: string): string => {
    const t = clause.trim().replace(/\s+/g, ' ');
    if (!t) return '';
    const capped = t.charAt(0).toUpperCase() + t.slice(1);
    return /[.!?]$/.test(capped) ? capped : `${capped}.`;
};

const toDisplay = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('en-AU') : '—';
    if (Array.isArray(v)) return v.length ? v.map(toDisplay).join(', ') : '—';
    if (typeof v === 'object') return escapeHtml(JSON.stringify(v));
    return escapeHtml(String(v));
};

function flattenObject(value: unknown, prefix = ''): { key: string; value: string }[] {
    if (value === null || value === undefined) return prefix ? [{ key: prefix, value: '—' }] : [];
    if (Array.isArray(value)) return value.flatMap((item, i) => flattenObject(item, `${prefix}[${i}]`));
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>)
            .filter(([k]) => !k.startsWith('__') && k !== 'firmAssumptions')
            .flatMap(([k, v]) => flattenObject(v, prefix ? `${prefix}.${k}` : k));
    }
    return [{ key: prefix, value: String(value) }];
}

const GOAL_LABELS: Record<string, string> = {
    home_care: 'Access care at home',
    aged_care: 'Move into residential aged care',
    downsize_home: 'Downsize the family home',
    retirement_village: 'Move to a retirement village',
    granny_flat: 'Set up a granny flat arrangement',
    meet_costs: 'Meet the costs',
    maximise_support: 'Maximise government support',
    preserve_assets: 'Preserve assets for the family',
    funding_guidance: 'Guidance on funding options',
};

/* ─── The snapshot ───────────────────────────────────────────────────────────────────────────── */

export interface SoaMoneyRow {
    label: string;
    amount: number;
    /** A short qualifier, e.g. "refundable less the departure fee". */
    note?: string;
}

export interface SoaPlanPiece {
    name: string;
    what: string;
    amount?: number | null;
    perYear?: boolean;
}

export interface SoaAlternative {
    name: string;
    description: string;
    pension: number;
    cashflow: number;
    estate: number;
    selected: boolean;
    /** Which goal it won, if any — "Best estate", "Best Age Pension". */
    badges: string[];
}

/** One line of a figure's working, as the engine reports it (`{label, value}`; value money or text). */
export interface SoaTipLine {
    label: string;
    value: string | number;
}

export interface SoaProjectionRow {
    label: string;
    pension: number;
    /** The module's recurring cost for the year (village fees, rent, support costs) — null where none. */
    moduleCost: number | null;
    cashflow: number;
    netWorth: number;
    /** A part year — the first row where the plan starts mid-year. */
    partial?: boolean;
    /** The day a partial row opened, as the engine serialised it. */
    periodStart?: string;
    /**
     * How each figure was worked out — the engine's own breakdown lines, shown on hover (owner,
     * 3 Oct 2026: the app's projection table explains every number; the document must too).
     */
    tips?: { pension?: SoaTipLine[]; moduleCost?: SoaTipLine[]; cashflow?: SoaTipLine[]; netWorth?: SoaTipLine[] };
}

/** The calendar the projection rows are cut on — the app's own toggle, read when the document is made. */
export type SoaPeriodMode = 'financial_year' | 'calendar_year';

/** An engine projection row, as every module's evaluator returns it (ProjectionYear, loosely). */
export interface SoaEngineRow {
    fyLabel?: unknown;
    isPartialFy?: unknown;
    periodStart?: unknown;
    socialSecurity?: unknown;
    agePensionPaidForPeriod?: unknown;
    socialSecurityForPartialPeriod?: unknown;
    socialSecurityBreakdown?: unknown;
    cashFlow?: unknown;
    netCashflow?: unknown;
    cashFlowForPartialPeriod?: unknown;
    cashFlowBreakdown?: unknown;
    cashflowRoutingLine?: unknown;
    netWorth?: unknown;
    netWorthBreakdown?: unknown;
    agedCareBreakdown?: unknown;
    investmentIncome?: unknown;
    investmentIncomeForPartialPeriod?: unknown;
    rentalIncome?: unknown;
    livingExpenses?: unknown;
    livingExpensesForPartialPeriod?: unknown;
    tax?: unknown;
    taxForPartialPeriod?: unknown;
    capitalExpenses?: unknown;
    home?: unknown;
    assets?: unknown;
    liabilities?: unknown;
    [key: string]: unknown;
}

const numOf = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};
const linesOf = (v: unknown): SoaTipLine[] | null =>
    Array.isArray(v) && v.length > 0
        ? (v as Array<{ label?: unknown; value?: unknown }>).filter((l) => l && typeof l === 'object').map((l) => ({ label: String(l.label ?? ''), value: typeof l.value === 'number' ? l.value : String(l.value ?? '') }))
        : null;

/**
 * The document's projection rows from the engine's, for every module: the period label the engine
 * cut the row on (`FY26/27`, or `2027` on the calendar toggle), the figures as the app's own table
 * shows them (a partial first row scaled to its days), and each figure's working for the hover —
 * the engine's `socialSecurityBreakdown` / `cashFlowBreakdown` / `netWorthBreakdown`, or the same
 * fallback lines the app's projection table builds when the engine sent none.
 */
export function projectionRowsFrom(
    list: readonly unknown[] | undefined,
    years: number,
    opts: { moduleCost?: (row: SoaEngineRow, index: number) => number | null; moduleCostLabel?: string } = {},
): SoaProjectionRow[] {
    return (list || []).slice(0, Math.max(years, 1)).map((raw, i) => {
        const r = (raw || {}) as SoaEngineRow;
        const partial = r.isPartialFy === true;
        const pensionAnnual = numOf(r.socialSecurity);
        const pension = numOf(r.agePensionPaidForPeriod ?? r.socialSecurityForPartialPeriod ?? r.socialSecurity);
        const cashflow = numOf(r.cashFlowForPartialPeriod ?? r.netCashflow ?? r.cashFlow);
        const netWorth = numOf(r.netWorth);
        const moduleCost = opts.moduleCost ? opts.moduleCost(r, i) : null;

        const pensionTip = linesOf(r.socialSecurityBreakdown) ?? [{ label: 'Age Pension (annual rate)', value: pensionAnnual }];
        if (partial && Math.abs(pension - pensionAnnual) > 1) pensionTip.push({ label: '---', value: '' }, { label: 'Paid across this part year', value: pension });

        const investment = numOf(r.investmentIncomeForPartialPeriod ?? r.investmentIncome);
        const rent = numOf(r.rentalIncome);
        const living = numOf(r.livingExpensesForPartialPeriod ?? r.livingExpenses);
        const tax = numOf(r.taxForPartialPeriod ?? r.tax);
        const capital = numOf(r.capitalExpenses);
        const costLabel = opts.moduleCostLabel || 'Module cost';
        const cashflowTip = linesOf(r.cashFlowBreakdown) ?? [
            { label: '--- INCOME ---', value: '' },
            { label: 'Age Pension', value: pension },
            { label: 'Investment income', value: investment },
            ...(rent > 0 ? [{ label: 'Rental income', value: rent }] : []),
            { label: '--- EXPENSES ---', value: '' },
            ...(moduleCost != null && moduleCost !== 0 ? [{ label: costLabel, value: -moduleCost }] : []),
            { label: 'Living expenses', value: -living },
            ...(tax !== 0 ? [{ label: 'Tax', value: -tax }] : []),
            ...(capital > 0 ? [{ label: 'Capital expenses', value: -capital }] : []),
            { label: '--- NET CASHFLOW ---', value: cashflow },
        ];
        const routing = r.cashflowRoutingLine as { label?: unknown; value?: unknown } | undefined;
        if (routing && typeof routing === 'object' && routing.label) cashflowTip.push({ label: '---', value: '' }, { label: String(routing.label), value: String(routing.value ?? '') });

        let netWorthTip = linesOf(r.netWorthBreakdown);
        if (!netWorthTip) {
            netWorthTip = [];
            const home = r.home as { value?: unknown; description?: unknown } | undefined;
            if (home && numOf(home.value) > 0) netWorthTip.push({ label: String(home.description || 'Family home'), value: numOf(home.value) });
            const assets = (r.assets || {}) as Record<string, unknown>;
            for (const [type, rows] of Object.entries(assets)) {
                if (!Array.isArray(rows)) continue;
                const total = rows.reduce((t: number, a: unknown) => t + numOf((a as { value?: unknown; balance?: unknown })?.value ?? (a as { balance?: unknown })?.balance), 0);
                if (total > 0) netWorthTip.push({ label: prettify(type), value: total });
            }
            const liabilities = (r.liabilities || {}) as Record<string, unknown>;
            const owed = Object.values(liabilities).reduce((t: number, rows) => t + (Array.isArray(rows) ? rows.reduce((u: number, l: unknown) => u + numOf((l as { balance?: unknown })?.balance), 0) : 0), 0);
            if (owed > 0) netWorthTip.push({ label: 'Less: debts', value: -owed });
            netWorthTip.push({ label: '=== NET WORTH ===', value: netWorth });
        }
        const moduleCostTip = moduleCost == null ? undefined : (linesOf(r.agedCareBreakdown) ?? [{ label: costLabel, value: moduleCost }]);

        return {
            label: r.fyLabel ? String(r.fyLabel) : `Year ${i + 1}`,
            pension, moduleCost, cashflow, netWorth,
            ...(partial ? { partial: true, periodStart: r.periodStart ? String(r.periodStart) : undefined } : {}),
            tips: { pension: pensionTip, cashflow: cashflowTip, netWorth: netWorthTip, ...(moduleCostTip ? { moduleCost: moduleCostTip } : {}) },
        };
    });
}

export interface SoaReplacement {
    /** "Your shares (about $120,000)" */
    from: string;
    /** "a lifetime annuity" */
    to: string;
    charges: string;
    consequences: string;
    lost: string;
}

export interface SoaImplication {
    title: string;
    lead: string;
    body: string;
}

/** One line of "what moves the estate" — the recap's list, with its amount and sign. */
export interface SoaEstateDriver {
    text: string;
    amount?: number | null;
    perYear?: boolean;
    sign: 'plus' | 'minus' | 'neutral';
}

export interface ModuleSoaSnapshot {
    documentKind: ModuleSoaKind;
    reportId: string;
    generatedAt: string;
    strategySource: 'suggestion_engine' | 'manual_control';
    /**
     * A hand-built plan that began as a tested one ("Customise this plan", owner 25 Oct 2026): that
     * plan's plain title, so the document can say the plan started there and was then changed by the
     * client. Absent for a plan built from blank, and for a tested plan.
     */
    customisedFrom?: string | null;
    /** Frozen at generation — who gave the advice, under whose licence. */
    adviceAttributionLine: string | null;
    adviceAttributionIncomplete: boolean;

    clientName: string;
    partnerName: string;
    clientAge: number | null;
    partnerAge: number | null;
    isCouple: boolean;
    completerDisplay: string;
    completerRelationship: string;
    goals: string[];
    /** The client's position as entered — "Your situation today" (soaKit.ts). */
    situation: SoaSituation;

    /** The decision the client brought — "You are weighing a move into a retirement village…". */
    decisionIntro: string;
    selectedStrategyName: string;
    selectedStrategyDescription: string;
    /** The plan in one plain line, every piece named: "Sell the home, buy a new home for …, put … into …" */
    inOneLine: string;
    /** What this strategy does, as the card said it, clause by clause. */
    inBrief: string[];
    pieces: SoaPlanPiece[];
    moneyFlow: SoaMoneyRow[];

    legacyYears: number;
    headline: { pension: number; cashflow: number; estate: number };
    baseline: { name: string; pension: number; cashflow: number; estate: number; netWorth?: number | null };
    /** Why it fits: the reasons behind the three figures, each a sentence. */
    whyItFits: string[];
    /** What moves the estate against the baseline — one driver per line (owner, 3 Oct 2026). */
    estateDrivers: SoaEstateDriver[];
    /** The client's own reason for the move, where the setup asked — said beside an estate that ends behind. */
    motiveNote: string | null;

    /** The module's own page — "What the village costs", "The sale and the new home", … */
    modulePage: { title: string; intro: string; rows: SoaMoneyRow[]; footnote?: string } | null;
    moduleCostLabel: string;
    projectionConvention: string;

    alternatives: SoaAlternative[];
    /** Every strategy modelled, for the sortable comparison table. */
    allEvaluated: SoaComparisonRow[];
    totalEvaluated: number;
    projections: SoaProjectionRow[];
    /** The baseline's own year-by-year path, drawn beside the plan's. Empty where none was projected. */
    baselineProjections: SoaProjectionRow[];
    /** The calendar the rows are cut on — the app's own toggle at the time the document was made. Absent = financial years. */
    periodMode?: SoaPeriodMode;
    replacements: SoaReplacement[];
    implications: SoaImplication[];
    strategyRisks: StrategyRisks;
    scope: { inScope: string[]; outOfScope: string[] };
    nextSteps: string[];

    ratesReviewNotice: string | null;
    annualLivingExpenses: number;
    assetRows: { assetType: string; description: string; value: number; incomeRate: number; growthRate: number; overridden: boolean; basis?: string }[];
    factFindSections: SoaFactFindSection[];
    allFactFindRows: { key: string; value: string }[];
    structureAppendixHtml: string;
    /** The rates and assumptions the advice rested on, frozen with the document (assumptionSections.ts). */
    assumptionSections: AssumptionSection[];
    /**
     * A plan with a guaranteed income for life: its own section, with what the family receives year by year,
     * the alternatives, the assumptions, the client's dated answers and, for income only, the confirmation
     * (soaGuaranteedIncome.ts; the guaranteed income review R1–R4, 21 Oct 2026). Absent: no such plan.
     */
    guaranteedIncome?: SoaGuaranteedIncome | null;
}

/** The fields every module fills the same way, from the file and the session. */
export function moduleSoaCommon(
    factFind: DraftFactFind,
    opts: { kind: ModuleSoaKind; strategySource: 'suggestion_engine' | 'manual_control'; riskKinds: readonly RiskKind[]; riskSpecifics?: StrategyRiskSpecifics; customisedFrom?: string | null },
): Pick<
    ModuleSoaSnapshot,
    | 'documentKind' | 'reportId' | 'generatedAt' | 'strategySource' | 'customisedFrom' | 'adviceAttributionLine' | 'adviceAttributionIncomplete'
    | 'clientName' | 'partnerName' | 'clientAge' | 'partnerAge' | 'isCouple' | 'completerDisplay' | 'completerRelationship' | 'goals' | 'situation'
    | 'strategyRisks' | 'ratesReviewNotice' | 'annualLivingExpenses' | 'assetRows' | 'factFindSections' | 'allFactFindRows' | 'structureAppendixHtml' | 'assumptionSections'
> {
    const fd = (factFind || {}) as DraftFactFind;
    const isCouple = fd.adviceFor === 'couple';
    const age = (dob: unknown): number | null => {
        if (!dob) return null;
        const d = new Date(String(dob));
        if (Number.isNaN(d.getTime())) return null;
        const now = new Date();
        let a = now.getFullYear() - d.getFullYear();
        if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a -= 1;
        return a;
    };
    const attribution = getCurrentAdviceAttribution();
    const structureAppendix = buildDigitalSoaStructureAppendix(fd as never, { strategySource: opts.strategySource });
    const prefix = { retirement_village: 'RV', downsizer: 'DZ', granny_flat: 'GF', cashflow_pension: 'CP' }[opts.kind];
    const completer = text(fd.completer);
    const completerName = [text(fd.completerFirstName), text(fd.completerLastName)].filter(Boolean).join(' ');

    return {
        documentKind: opts.kind,
        reportId: `${prefix}-${Date.now().toString(36).toUpperCase()}`,
        generatedAt: new Date().toISOString(),
        strategySource: opts.strategySource,
        customisedFrom: opts.strategySource === 'manual_control' && opts.customisedFrom ? opts.customisedFrom : null,
        adviceAttributionLine: attribution?.line ?? null,
        adviceAttributionIncomplete: attribution ? attribution.complete === false : false,
        clientName: text(fd.client?.firstName) || 'Client',
        partnerName: isCouple ? text(fd.partner?.firstName) : '',
        clientAge: age(fd.client?.dob),
        partnerAge: isCouple ? age(fd.partner?.dob) : null,
        isCouple,
        completerDisplay: completer && completer !== 'client' ? completerName : '',
        completerRelationship: completer && completer !== 'client' ? text(fd.completerRelationship) : '',
        // Only a key with a goal label is a goal: the same record carries on/off flags (rvPrimerSeen,
        // sahSetupSeen, …) that were printed as goals (24 Oct 2026).
        goals: Object.entries((fd.goals || {}) as Record<string, unknown>)
            .filter(([key, on]) => on === true && GOAL_LABELS[key])
            .map(([key]) => GOAL_LABELS[key]),
        situation: situationFrom(fd),
        strategyRisks: buildStrategyRisks(opts.riskKinds, opts.riskSpecifics),
        ratesReviewNotice: ratesReviewStatus().notice ?? null,
        annualLivingExpenses: num(fd.expenses?.livingExpenses),
        assetRows: Object.entries((fd.assets || {}) as Record<string, Array<Record<string, unknown>> | undefined>).flatMap(([assetType, assets]) =>
            (assets || []).map((asset) => ({
                assetType: prettify(assetType),
                description: text(asset.description) || 'Unnamed asset',
                value: num(asset.value),
                ...assetAssumptionFiguresFor(assetType, asset),
            })),
        ),
        factFindSections: buildFactFindSections(fd),
        allFactFindRows: flattenObject(fd),
        structureAppendixHtml: structureAppendix ? buildDigitalSoaStructureAppendixHtml(structureAppendix, formatCurrency as never) : '',
        assumptionSections: buildAssumptionSections(),
    };
}

/** The appendix groups a module's reader needs open; the rest stay collapsed. */
const OPEN_ASSUMPTIONS: Record<ModuleSoaKind, RegExp> = {
    retirement_village: /How the Plans|Social Security|Asset & Market|Superannuation/i,
    downsizer: /How the Plans|Social Security|Tax|Asset & Market|Superannuation/i,
    granny_flat: /How the Plans|Social Security|Asset & Market|Superannuation/i,
    cashflow_pension: /How the Plans|Social Security|Tax|Asset & Market|Superannuation/i,
};

/* ─── The document ───────────────────────────────────────────────────────────────────────────── */

const money = (n: number): string => formatCurrency(n);
const delta = (after: number, before: number, upIsGood = true): string => {
    const d = after - before;
    if (Math.abs(d) < 1) return '<span class="muted">no change</span>';
    const good = upIsGood ? d > 0 : d < 0;
    return `<span class="${good ? 'age-pos' : 'age-neg'}">${d > 0 ? '+' : '−'}${money(Math.abs(d))}</span>`;
};

function kpi(label: string, value: number, baseline: number, upIsGood: boolean, baselineName: string): string {
    const d = value - baseline;
    const cls = Math.abs(d) < 1 ? 'kpi' : (upIsGood ? d > 0 : d < 0) ? 'kpi kpi--gain' : 'kpi kpi--warn';
    return `<div class="${cls}"><div class="label">${label}</div><div class="value">${money(value)}</div><div class="tiny muted" style="margin-top:4px">${escapeHtml(baselineName)}: ${money(baseline)} · ${delta(value, baseline, upIsGood)}</div></div>`;
}

const section = (title: string, body: string): { title: string; html: string } => ({ title, html: `<section class="page"><div class="card">\n<div class="h2">${title}</div>\n${body}\n</div></section>` });

export function buildModuleSoaHtml(s: ModuleSoaSnapshot): string {
    const coverA = stockPhoto('1559827260-dc66d52bef19', 1920, 1080);
    const coverB = stockPhoto('1506905925346-21bda4d32df4', 1920, 1080);
    const names = `${escapeHtml(s.clientName)}${s.partnerName ? ` &amp; ${escapeHtml(s.partnerName)}` : ''}`;
    const you = s.isCouple ? 'you both' : 'you';
    const baselineWord = s.baseline.name.toLowerCase();
    // Claiming, or reporting, the pension change the plan rests on (owner, 3 Oct 2026).
    const pension = pensionCallout(s.headline.pension, s.baseline.pension);
    const implications = pension ? [pension.implication, ...s.implications] : s.implications;
    const nextSteps = pension ? [...s.nextSteps, pension.nextStep] : s.nextSteps;
    const ratesNotice = s.ratesReviewNotice
        ? `<li><strong>Rates and thresholds</strong> — ${escapeHtml(s.ratesReviewNotice)}</li>`
        : '';
    const attribution = s.adviceAttributionLine
        ? `${escapeHtml(s.adviceAttributionLine)}${s.adviceAttributionIncomplete ? ' <strong>Your firm profile is incomplete — finish it in Settings before issuing this to a client.</strong>' : ''}`
        : 'AgedCareWise Pty Ltd (Demo AFSL 401234) — ensure this block matches your licensee’s approved wording before client issue.';

    const goalsBlock = s.goals.length
        ? `<ul class="goalsList">${s.goals.map((g) => `<li>${escapeHtml(g)}</li>`).join('')}</ul>`
        : '<p class="muted">No goals were ticked in the profile, so this advice is built on the module you chose and the figures you gave.</p>';

    const altCards = s.alternatives
        .map(
            (a) => `<div class="opt-card${a.selected ? ' opt-card--selected' : ''}">
<div class="opt-card-head">${a.badges.map((b) => `<span class="opt-tag">${escapeHtml(b)}</span>`).join('')}
<span class="opt-card-status">${a.selected ? '<span class="pill-selected">Your choice</span>' : '<span class="pill-not-selected">Considered</span>'}</span></div>
<h4 class="opt-title">${escapeHtml(a.name)}</h4>
<div class="opt-card-main"><p class="opt-narr">${escapeHtml(a.description)}</p>
<div class="opt-metrics">
<div class="opt-metric-line"><span>Estate after ${s.legacyYears} years</span><strong>${money(a.estate)}</strong></div>
<div class="opt-metric-line"><span>Age Pension a year</span><strong>${money(a.pension)}</strong></div>
<div class="opt-metric-line"><span>Cashflow a year</span><strong>${money(a.cashflow)}</strong></div>
</div></div></div>`,
        )
        .join('');

    /*
     * THE FIGURES EXPLAIN THEMSELVES (owner, 3 Oct 2026). Every number in the projection table
     * carries its working on hover — the engine's own breakdown, as the app's projection table shows
     * it — with no "i" icon: the dotted underline is the affordance, and the cell takes focus so a
     * keyboard or a tap opens it. Print shows the figures alone.
     */
    const tipValue = (v: string | number): string => {
        if (typeof v === 'number') return `${v < 0 ? '−' : ''}${money(Math.abs(v))}`;
        return escapeHtml(v);
    };
    const tipLines = (lines: SoaTipLine[] | undefined): string => (lines || [])
        .map((l) => {
            const label = String(l.label || '');
            if (/^---$/.test(label.trim())) return '<div class="tip-rule"></div>';
            const heading = /^(---|===)/.test(label);
            return `<div class="tip-row${heading ? ' tip-row--head' : ''}"><span class="tip-lbl">${escapeHtml(label.replace(/^[-=\s]+|[-=\s]+$/g, ''))}</span><span class="tip-val">${l.value === '' ? '' : tipValue(l.value)}</span></div>`;
        })
        .join('');
    // The figure is the trigger, on hover, focus or tap (owner, 24 Oct 2026).
    let tipSeq = 0;
    const cell = (valueHtml: string, title: string, lines: SoaTipLine[] | undefined, up: boolean): string => {
        if (!lines || !lines.length) return valueHtml;
        const id = `soa-cell-tip-${++tipSeq}`;
        return `<span class="soa-cell" tabindex="0" role="button" aria-describedby="${id}">${valueHtml}<span class="soa-cell-tip${up ? ' up' : ''}" id="${id}" role="tooltip"><strong>${escapeHtml(title)}</strong>${tipLines(lines)}</span></span>`;
    };
    const projRows = s.projections
        .map((r, i) => {
            const up = i >= Math.ceil(s.projections.length / 2);
            const yearCell = `${escapeHtml(r.label)}${r.partial ? `<span class="tiny muted" style="display:block;font-weight:400">${r.periodStart ? `from ${escapeHtml(periodOpenedOn(r.periodStart))} ` : ''}(part year)</span>` : ''}`;
            return `<tr class="proj-tr-data${i % 2 ? ' proj-zebra' : ''}"><td class="proj-year">${yearCell}</td><td class="td-num">${cell(money(r.pension), 'Age Pension — how it was worked out', r.tips?.pension, up)}</td>${s.moduleCostLabel ? `<td class="td-num">${r.moduleCost == null ? '—' : cell(money(r.moduleCost), `${s.moduleCostLabel} — how it was worked out`, r.tips?.moduleCost, up)}</td>` : ''}<td class="td-num ${r.cashflow < 0 ? 'age-neg' : ''}">${cell(money(r.cashflow), 'Cashflow — money in less money out', r.tips?.cashflow, up)}</td><td class="td-num">${cell(money(r.netWorth), 'Net worth at the end of the year', r.tips?.netWorth, up)}</td></tr>`;
        })
        .join('');
    const calendarNote = s.periodMode === 'calendar_year'
        ? 'The rows are calendar years (January to December), as chosen in the app.'
        : 'The rows are financial years (July to June), as chosen in the app.';
    const partYearNote = s.projections.some((r) => r.partial) ? ' The first row runs from the start date to the end of that year, so it is a part year: its figures cover only those months.' : '';
    const charts = renderProjectionCharts({
        idPrefix: 'soa-proj',
        planName: s.selectedStrategyName,
        plan: s.projections,
        baselineName: s.baseline.name,
        baseline: s.baselineProjections,
    });

    const estateDrivers = s.estateDrivers.length
        ? `<div class="h3">What moves the estate</div>
<p class="small muted">Both figures are projected over the same ${s.legacyYears} years — this plan against ${escapeHtml(baselineWord)} — so they can be compared directly. One driver per line.</p>
<ul class="soa-drivers">${s.estateDrivers.map((d) => `<li><span>${escapeHtml(d.text)}</span>${d.amount != null && d.amount > 0 ? `<strong class="${d.sign}">${d.sign === 'minus' ? '−' : d.sign === 'plus' ? '+' : ''}${money(d.amount)}${d.perYear ? ' a year' : ''}</strong>` : `<strong class="${d.sign}">${d.sign === 'minus' ? 'lower' : d.sign === 'plus' ? 'higher' : '—'}</strong>`}</li>`).join('')}</ul>`
        : '';
    const motiveBlock = s.motiveNote
        ? `<div class="callout callout--blue" style="margin-top:14px"><p class="callout-title">A lifestyle decision, not only a financial one</p><p class="small" style="margin:0">${escapeHtml(s.motiveNote)}</p></div>`
        : '';

    const pages: Array<{ title: string; html: string }> = [];

    pages.push(section('Executive summary', `
<p class="lede">${escapeHtml(s.decisionIntro)}</p>
<p>${s.strategySource === 'manual_control' && s.customisedFrom
    ? `<strong>How this plan was made:</strong> you started from the suggested plan <strong>“${escapeHtml(s.customisedFrom)}”</strong> and changed it yourself, into <strong>${escapeHtml(s.selectedStrategyName)}</strong>. This Statement of Advice follows that plan through to its projected outcomes.`
    : s.strategySource === 'manual_control'
    ? `<strong>How this plan was made:</strong> you built <strong>${escapeHtml(s.selectedStrategyName)}</strong> yourself. This Statement of Advice follows that plan through to its projected outcomes.`
    : `<strong>How this plan was made:</strong> AgedCareWise found this plan by testing <strong>${s.totalEvaluated}</strong> ways of doing this, each measured against <strong>${escapeHtml(baselineWord)}</strong>. You chose <strong>${escapeHtml(s.selectedStrategyName)}</strong>, and this Statement of Advice follows that plan through to its projected outcomes.`}</p>
<div class="callout callout--blue">
<p class="callout-title">Our advice, in one line</p>
<p class="oneLine">${escapeHtml(sentence(s.inOneLine))}</p>
</div>
<div class="h3">What it means for ${you}</div>
<p class="small muted">The three figures that matter, each against ${escapeHtml(baselineWord)}. The year-by-year path is in the projection section.</p>
<div class="grid3">
${kpi('Age Pension a year', s.headline.pension, s.baseline.pension, true, s.baseline.name)}
${kpi('Cashflow a year', s.headline.cashflow, s.baseline.cashflow, true, s.baseline.name)}
${kpi(`Estate after ${s.legacyYears} years`, s.headline.estate, s.baseline.estate, true, s.baseline.name)}
</div>
<div class="h3">What you told us you are trying to achieve</div>
${goalsBlock}
<div class="h3">In this document</div>
<div id="soa-toc-slot"></div>`));

    pages.push(section('Scope of advice', `
<p>This section sets clear boundaries: what this advice covers, what it does not, and where you still need a specialist (for example a solicitor or a tax agent).</p>
<div class="scope-grid">
<div class="scope-in"><h4>In scope</h4><ul>${s.scope.inScope.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}
<li>How the years are shown: ${escapeHtml(s.projectionConvention)}</li></ul></div>
<div class="scope-out"><h4>Out of scope</h4><ul>${s.scope.outOfScope.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}
<li>Specific financial product advice — recommending a particular fund, insurer, lender or security.</li>
<li>Tax, legal or estate implementation — capital gains tax calculations, trust structuring, or drafting Wills and powers of attorney.</li>
<li>Product providers' fees, break fees, exit fees and surrender terms, which must be confirmed with each provider before you act.</li></ul></div>
</div>
<p class="small muted">If your circumstances fall outside these boundaries, update the profile or seek a qualified specialist before acting.</p>`));

    pages.push(section('Your situation today', `
<p class="lede">This advice is based on what ${you} told us. If anything here is wrong or has changed, the advice may not suit ${you}: update the profile and we will re-run it.</p>
<p class="small muted">The four numbers are ${escapeHtml(baselineWord)} — the position every strategy in this document is measured against.</p>
${renderSituationHtml(s.situation, { ...s.baseline }, s.legacyYears)}
<p class="small muted" style="margin-top:12px">The full list of what you entered, and the rates we applied, is in the appendices at the end of this document.</p>`));

    if (s.modulePage) {
        pages.push(section(escapeHtml(s.modulePage.title), `
<p class="lede">${escapeHtml(s.modulePage.intro)}</p>
<table><tbody>${s.modulePage.rows.map((r) => `<tr><td>${escapeHtml(r.label)}${r.note ? `<div class="tiny muted">${escapeHtml(r.note)}</div>` : ''}</td><td class="td-num" style="text-align:right;font-weight:700">${money(r.amount)}</td></tr>`).join('')}</tbody></table>
${s.modulePage.footnote ? `<p class="small muted">${escapeHtml(s.modulePage.footnote)}</p>` : ''}`));
    }

    pages.push(section('What we recommend', `
<p class="lede"><strong>${escapeHtml(s.selectedStrategyName)}.</strong> ${escapeHtml(s.selectedStrategyDescription)}</p>
<div class="h3">The pieces of the plan</div>
<ul class="pieces">${s.pieces.map((p) => `<li class="piece"><p class="piece-name">${escapeHtml(p.name)}${p.amount != null && p.amount > 0 ? `<span class="piece-amt">${money(p.amount)}${p.perYear ? ' a year' : ''}</span>` : ''}</p><p class="piece-what">${escapeHtml(p.what)}</p></li>`).join('')}</ul>
${s.moneyFlow.length ? `<div class="h3">Where the money goes</div><ul class="flow">${s.moneyFlow.map((r) => `<li><span>${escapeHtml(r.label)}${r.note ? `<div class="tiny muted">${escapeHtml(r.note)}</div>` : ''}</span><strong>${money(r.amount)}</strong></li>`).join('')}</ul>` : ''}
<div class="h3">What this strategy does</div>
<ul class="riskList">${s.inBrief.map((c) => `<li>${escapeHtml(sentence(c))}</li>`).join('')}</ul>`));

    if (s.guaranteedIncome) pages.push(section('Guaranteed income for life', renderGuaranteedIncomeSectionHtml(s.guaranteedIncome)));

    pages.push(section(`Why this advice fits ${you}`, `
<p class="lede">We compared every plan on the three things ${you} asked about — the Age Pension, the money left each year, and what is left to the family — against ${escapeHtml(baselineWord)}.</p>
<table><thead><tr><th></th><th>${escapeHtml(s.baseline.name)}</th><th>${escapeHtml(s.selectedStrategyName)}</th><th>Change</th></tr></thead><tbody>
<tr><td>Age Pension a year</td><td>${money(s.baseline.pension)}</td><td><strong>${money(s.headline.pension)}</strong></td><td>${delta(s.headline.pension, s.baseline.pension)}</td></tr>
<tr><td>Cashflow a year</td><td>${money(s.baseline.cashflow)}</td><td><strong>${money(s.headline.cashflow)}</strong></td><td>${delta(s.headline.cashflow, s.baseline.cashflow)}</td></tr>
<tr><td>Estate after ${s.legacyYears} years</td><td>${money(s.baseline.estate)}</td><td><strong>${money(s.headline.estate)}</strong></td><td>${delta(s.headline.estate, s.baseline.estate)}</td></tr>
</tbody></table>
<ul class="riskList" style="margin-top:14px">${s.whyItFits.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>
${estateDrivers}
${motiveBlock}`));

    if (implications.length) {
        pages.push(section('Implications of this advice', `
<p class="lede">Before you act on any part of this plan, these are the practical points to know — including the assumptions the figures rest on, which you can change in the app and re-run.</p>
<div class="imp-grid">${implications.map((i) => `<div class="imp-card"><p class="imp-title">${escapeHtml(i.title)}</p><p class="imp-lead">${escapeHtml(i.lead)}</p><p class="imp-body">${escapeHtml(i.body)}</p></div>`).join('')}</div>`));
    }

    if (s.replacements.length) {
        pages.push(section('If this advice replaces one investment with another', `
<p class="lede">Where the plan sells or draws on something you hold to buy something else, the law asks us to spell out what that costs, what changes, and what you give up.</p>
<table><thead><tr><th>From</th><th>To</th><th>What it costs</th><th>What changes</th><th>What you give up</th></tr></thead><tbody>
${s.replacements.map((r) => `<tr><td>${escapeHtml(r.from)}</td><td>${escapeHtml(r.to)}</td><td>${escapeHtml(r.charges)}</td><td>${escapeHtml(r.consequences)}</td><td>${escapeHtml(r.lost)}</td></tr>`).join('')}
</tbody></table>
<p class="small muted">Product fees, exit fees and surrender terms are set by each provider and are not modelled here; confirm them with the provider before acting.</p>`));
    }

    /*
     * The three cards are the comparison the law asks for; the full list of everything modelled is
     * kept for the record but folded away (owner, 3 Oct 2026: 34 rows is a laundry list, and the
     * cashflow module can run to hundreds).
     */
    pages.push(section('Alternative strategies considered', `
<p class="lede">We modelled ${s.totalEvaluated} ways of doing this. These are the ones that led on each goal, beside the one you chose, so you can see what you would be trading.</p>
<div class="opt-grid">${altCards}</div>
${s.allEvaluated.length > 1 ? `<details class="factSection soa-all-strategies" data-print-closed><summary><strong>Every strategy modelled (${s.allEvaluated.length})</strong> <span class="small muted">— open to compare them all; sort by any column. The benchmark stays at the top; what-ifs are listed separately and are not recommendations.</span></summary>
${renderComparisonTable(s.allEvaluated, s.legacyYears, s.baseline)}</details>` : ''}`));

    pages.push(section('Projection outlook', `
<p class="muted small">Year by year for <strong>${escapeHtml(s.selectedStrategyName)}</strong>${s.baselineProjections.length ? ` against <strong>${escapeHtml(baselineWord)}</strong>` : ''}. ${escapeHtml(calendarNote)}${escapeHtml(partYearNote)} Hover over a year in the chart for its figures.</p>
${charts}
<div class="h3">The figures</div>
<p class="small muted screen-only">Hover over any figure, or tap it, to see how it was worked out.</p>
<table class="table-proj"><thead><tr><th>Year</th><th>Age Pension</th>${s.moduleCostLabel ? `<th>${escapeHtml(s.moduleCostLabel)}</th>` : ''}<th>Cashflow</th><th>Net worth</th></tr></thead><tbody>${projRows}</tbody></table>
<p class="tiny muted">Net worth is everything owned less everything owed at the end of each year. A negative cashflow is drawn from savings, which is why net worth can fall even while the pension rises. These are illustrations on the assumptions in the appendix, not forecasts.</p>`));

    if (s.strategyRisks.sections.length) {
        pages.push(section('What could go wrong with this strategy', `
<p class="lede">Every plan has trade-offs. These are the ones that matter for the strategy recommended here, as they were shown to you when you chose it. None of them means the plan is wrong; they are the points to be comfortable with before you act.</p>
${s.strategyRisks.forYou.length ? `<div class="callout callout--amber"><p class="callout-title">For ${you} specifically</p><ul class="riskList">${s.strategyRisks.forYou.map((p) => `<li><strong>${escapeHtml(p.lead)}</strong> ${escapeHtml(p.body)}</li>`).join('')}</ul></div>` : ''}
${s.strategyRisks.sections.map((sec) => `<div class="h3">${escapeHtml(sec.title)}</div><ul class="riskList">${sec.points.map((p) => `<li><strong>${escapeHtml(p.lead)}</strong> ${escapeHtml(p.body)}</li>`).join('')}</ul>`).join('')}`));
    }

    pages.push(section('Risks &amp; limitations', `
<ul class="riskList">
${ratesNotice}<li><strong>Legislation and policy</strong> — Age Pension, superannuation, aged care and state property rules can change, which can shift these outcomes.</li>
<li><strong>Modelling versus reality</strong> — the figures depend on assumptions about returns, fees, growth and timing, listed in the appendix. Real outcomes will differ.</li>
<li><strong>Information gaps</strong> — if the profile is incomplete or out of date, this advice may not suit you. Update it and re-run the analysis when anything material changes.</li>
<li><strong>Timing</strong> — sale dates, settlement, Centrelink reassessment and tax-year boundaries can all differ from the model's timing and create part-year differences.</li>
<li><strong>Tax and legal</strong> — capital gains tax and estate outcomes should be confirmed with a registered tax agent or solicitor before you sell assets or sign anything. Where stamp duty is shown it is estimated from the state's published scale with no concessions applied.</li>
</ul>`));

    pages.push(section('Fees &amp; disclosures', `
<p><strong>Who is giving this advice:</strong> ${attribution}</p>
<p><strong>Advice fee:</strong> Initial fee <strong>$450 plus GST</strong>. <strong>No ongoing advice fee</strong> and <strong>no separate implementation fee</strong> is charged for this digital pathway.</p>
<p><strong>Other costs:</strong> Village operators, lenders, insurers and investment products charge their own fees, disclosed in their contracts, PDS or platform schedules.</p>
<p><strong>Commissions and benefits:</strong> We receive no commission, referral payment or other benefit from any village, lender, insurer, product issuer or agent in connection with this advice.</p>
<p><strong>Conflicts of interest:</strong> AgedCareWise does <strong>not</strong> provide specific financial <em>product</em> advice. Recommendations are <strong>strategic</strong> and illustrated through the engine's assumptions. Where the plan draws on particular assets, that ordering reflects <strong>liquidity, growth and tax assumptions you supplied</strong> — not a commission or conflict tied to a product issuer. On that basis, <strong>no conflicts are disclosed</strong> in relation to this advice.</p>
<p><strong>Complaints:</strong> Contact AgedCareWise using the details on your account or correspondence. If a complaint cannot be resolved, you may refer the matter to the Australian Financial Complaints Authority (AFCA) where eligible.</p>`));

    pages.push(section('Next steps', `
<ul class="riskList">${nextSteps.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}
<li><strong>Re-run the analysis</strong> whenever something significant changes, so the pension, cashflow and estate figures stay current.</li></ul>`));

    pages.push(section('Appendix A — what you told us', `
<p><strong>Client age:</strong> ${s.clientAge ?? '—'}${s.partnerName ? ` | <strong>Partner age:</strong> ${s.partnerAge ?? '—'}` : ''} | <strong>Living expenses:</strong> ${money(s.annualLivingExpenses)} a year</p>
<div class="h3">Your assets and the rates applied to each</div>
<table><thead><tr><th>Asset type</th><th>Description</th><th>Value</th><th>Income %</th><th>Growth %</th><th>Client's own figures</th></tr></thead><tbody>
${s.assetRows.map((row) => `<tr><td>${escapeHtml(row.assetType)}</td><td>${escapeHtml(row.description)}${row.basis ? `<div class="tiny muted">${escapeHtml(row.basis)}</div>` : ''}</td><td>${money(row.value)}</td><td>${row.incomeRate.toFixed(2)}%</td><td>${row.growthRate.toFixed(2)}%</td><td>${row.overridden ? 'Yes' : 'No'}</td></tr>`).join('')}
</tbody></table>
<p class="tiny muted">Income % is what the model applied. For an investment property it is the yield the recorded rent represents — the projections use the rent and expense dollars recorded, not an assumed yield.</p>
${s.structureAppendixHtml || ''}
<div class="h3">Structured fact-find responses</div>
${s.factFindSections.map((sec) => `<details class="factSection"><summary><strong>${escapeHtml(sec.title)}</strong></summary><div>${sec.rows.map((row) => `<div class="factRow"><div class="factLabel">${escapeHtml(row.label)}</div><div>${toDisplay(row.value)}</div></div>`).join('')}</div></details>`).join('')}`));
    // The raw field dump ("technical full snapshot") is not for the client (owner, 3 Oct 2026); the
    // advice register seals the file the document was built from, and that is the audit record.

    pages.push(section('Appendix B — the rates and assumptions we applied', `
<p class="lede">Every figure in this document rests on these. Year 1 uses the legislated rates and thresholds; later years index them at the projection inflation rate. The groups most relevant to this advice are open; the rest are here for completeness.</p>
${renderAssumptionsAppendixHtml(s.assumptionSections, OPEN_ASSUMPTIONS[s.documentKind])}`));

    const toc = renderToc(pages.map((p) => p.title.replace(/&amp;/g, '&')));
    const body = pages.map((p, i) => (i === 0 ? p.html.replace('<div id="soa-toc-slot"></div>', toc).replace('<section class="page">', '<section class="page active">') : p.html)).join('\n\n');

    return `<!DOCTYPE html><html lang="en-AU"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Statement of Advice — ${escapeHtml(s.clientName)}</title>
<style>
:root{--ink:#0f172a;--muted:#64748b;--line:#e2e8f0;--card:#fff;--accent:#2563eb;--accent2:#4f46e5;--warn-bg:#fff7ed;--warn-border:#fdba74;--warn-ink:#9a3412;--radius:16px;--shadow:0 14px 40px rgba(15,23,42,.08)}
body{margin:0;font-family:Inter,Segoe UI,system-ui,sans-serif;background:var(--ink);color:var(--ink);line-height:1.55}
.cover{min-height:100vh;display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden}
.cover::before{content:'';position:absolute;inset:0;background-image:url('${coverA}');background-size:cover;background-position:center;opacity:.38;animation:moveA 28s ease-in-out infinite}
.cover::after{content:'';position:absolute;inset:0;background-image:url('${coverB}');background-size:cover;background-position:center;opacity:.22;animation:moveB 34s ease-in-out infinite}
.coverShade{position:absolute;inset:0;background:radial-gradient(120% 80% at 30% 20%,rgba(37,99,235,.45),rgba(15,23,42,.88))}
.coverBox{position:relative;z-index:1;max-width:1040px;width:88%;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.35);border-radius:28px;padding:40px 36px;color:#fff;backdrop-filter:blur(8px);box-shadow:0 24px 80px rgba(0,0,0,.35)}
.coverKicker{font-size:12px;letter-spacing:.22em;text-transform:uppercase;opacity:.9}
.coverTitle{font-size:clamp(36px,6vw,58px);line-height:1.05;font-weight:900;margin:10px 0 8px}
.clientNames{font-size:clamp(22px,3.5vw,32px);font-weight:700}
.meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:18px}
.meta span{font-size:11px;padding:8px 12px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.4)}
.openBtn{margin-top:26px;padding:14px 22px;border-radius:14px;border:none;font-weight:800;cursor:pointer;background:#fff;color:var(--ink);font-size:15px;box-shadow:0 8px 24px rgba(0,0,0,.2);transition:transform .15s ease}
.openBtn:hover{transform:translateY(-2px)}
.book{background:#f8fafc;max-width:1200px;margin:0 auto;padding:28px 20px 48px;scroll-margin-top:0;min-height:100vh}
#soa-document{scroll-margin-top:0}
.topNav{position:sticky;top:0;background:rgba(255,255,255,.92);border:1px solid var(--line);border-radius:14px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center;z-index:40;backdrop-filter:blur(10px);box-shadow:var(--shadow);margin-bottom:18px}
.topNav .buttons{display:flex;gap:8px;flex-wrap:wrap}
.topNav button{padding:9px 14px;min-width:90px;border:1px solid #c7d2fe;background:#fff;border-radius:10px;cursor:pointer;font-weight:700;font-size:13px;color:var(--ink)}
.topNav button:hover{border-color:var(--accent);color:var(--accent)}
.pager{font-size:12px;font-weight:700;color:var(--muted)}
.topNav .navEnd{display:flex;align-items:center;gap:12px;flex-wrap:wrap;justify-content:flex-end}
.topNav .printBtn{border-color:var(--navy,#001E7A);color:var(--navy,#001E7A)}
.page{display:none;animation:fadeIn .35s ease}
.page.active{display:block}
@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:26px 24px;margin-top:14px;box-shadow:var(--shadow);position:relative;overflow:visible}
.card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#6366f1,#3b82f6);opacity:.9}
.h2{font-size:26px;font-weight:900;margin:0 0 12px;letter-spacing:-.02em}
.h3{font-size:17px;font-weight:800;margin:22px 0 10px;color:#1e293b}
.lede{font-size:16px;margin:10px 0 14px}
.muted{color:var(--muted)}.small{font-size:13px}.tiny{font-size:12px}
.goalsList{margin:8px 0 16px;padding-left:20px}
.goalsList li{margin:6px 0}
.grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}
.kpi{border:1px solid var(--line);border-radius:12px;padding:14px 12px}
.label{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;font-weight:700}
.value{font-size:24px;font-weight:900;margin-top:4px}
.callout{border-radius:14px;padding:16px 18px;margin:16px 0}
.callout-title{font-weight:800;margin:0 0 8px;font-size:15px}
.oneLine{font-size:20px;font-weight:800;line-height:1.35;margin:6px 0 14px;color:#1e293b}
.pieces{list-style:none;margin:8px 0 0;padding:0;display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:10px}
.piece{border:1px solid var(--line);border-radius:12px;padding:12px 14px}
.piece-name{font-weight:800;font-size:14px;margin:0 0 4px}
.piece-amt{color:var(--accent);font-weight:800;margin-left:6px}
.piece-what{font-size:13px;color:#475569;margin:0}
.flow{margin:10px 0 0;padding:0;list-style:none}
.flow li{display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-bottom:1px solid #f1f5f9;font-size:14px}
.flow li strong{white-space:nowrap}
.age-pos{color:#16a34a}.age-neg{color:#dc2626}
.imp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;margin-top:10px}
.imp-card{border-radius:12px;padding:14px}
.imp-title{font-size:14px;font-weight:800;margin:0 0 8px}
.imp-lead{font-size:13px;color:#0f172a;margin:0 0 6px}
.imp-body{font-size:13px;color:#334155;margin:0}
.table-proj{font-variant-numeric:tabular-nums}
.table-proj .td-num{white-space:nowrap;position:relative}
.proj-year{white-space:nowrap;font-weight:700;color:#1f2937}
.soa-cell{position:relative;display:inline-block;cursor:help;text-decoration:underline dotted #94a3b8;text-decoration-thickness:1.5px;text-underline-offset:4px;outline:none}
.soa-cell:hover,.soa-cell:focus-visible{text-decoration-color:#2563eb}
.soa-cell-tip{position:absolute;left:50%;top:26px;transform:translateX(-50%);width:max-content;min-width:240px;max-width:min(360px,90vw);padding:12px 14px;border-radius:12px;border:0;background:#0F2A4A;color:#fff;box-shadow:0 14px 34px rgba(15,42,74,.35);font-size:12px;line-height:1.35;display:none;z-index:60;text-align:left;font-weight:400;white-space:normal;cursor:auto}
.soa-cell-tip::before{content:"";position:absolute;top:-5px;left:50%;width:10px;height:10px;background:#0F2A4A;transform:translateX(-50%) rotate(45deg)}
.soa-cell-tip.up{top:auto;bottom:26px}
.soa-cell-tip.up::before{top:auto;bottom:-5px}
/* The last column's bubble hangs to the left of its figure so it never runs off the card. */
.table-proj td:last-child .soa-cell-tip{left:auto;right:0;transform:none}
.table-proj td:last-child .soa-cell-tip::before{left:auto;right:14px;transform:rotate(45deg)}
.soa-cell-tip strong{display:block;color:#7dd3fc;margin-bottom:8px;font-size:13px}
.soa-cell-tip .tip-row{display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:2px 0}
.soa-cell-tip .tip-lbl{flex:1 1 auto;min-width:0;white-space:normal;overflow-wrap:anywhere}
.soa-cell-tip .tip-row--head{margin-top:4px;color:#cbd5e1;font-weight:700;letter-spacing:.04em;text-transform:uppercase;font-size:10.5px}
.soa-cell-tip .tip-lbl{color:#cbd5e1}
.soa-cell-tip .tip-val{font-variant-numeric:tabular-nums;white-space:nowrap;font-weight:700}
.soa-cell-tip .tip-rule{border-top:1px solid rgba(148,163,184,.35);margin:5px 0}
.soa-cell:hover .soa-cell-tip,.soa-cell:focus .soa-cell-tip,.soa-cell:focus-within .soa-cell-tip,.soa-cell.is-open .soa-cell-tip{display:block}
.soa-all-strategies>summary{cursor:pointer}
.scope-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:12px}
.scope-in h4,.scope-out h4{margin:0 0 8px;font-size:13px;text-transform:uppercase;letter-spacing:.06em}
.scope-in ul,.scope-out ul{margin:0;padding-left:18px;font-size:14px}
.opt-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:14px;align-items:stretch}
.opt-card{border-radius:14px;padding:16px;display:flex;flex-direction:column;min-height:100%;box-sizing:border-box}
.opt-card-head{display:flex;flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:8px;width:100%}
.opt-card-head .opt-tag{display:inline-block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;padding:4px 8px;border-radius:999px}
.pill-not-selected{font-size:10px;font-weight:800;padding:4px 10px;border-radius:999px}
.pill-selected{font-size:10px;font-weight:800;padding:4px 10px;border-radius:999px}
.opt-title{font-size:16px;margin:0 0 10px;font-weight:800;line-height:1.25}
.opt-card-main{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}
.opt-narr{font-size:14px;color:#475569;margin:0;flex:1 1 auto;line-height:1.45}
.opt-metrics{margin-top:auto;padding-top:14px;border-top:1px solid #e2e8f0;display:grid;grid-template-columns:1fr;gap:8px;font-size:14px}
.opt-metric-line{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.opt-metric-line strong{font-size:20px;line-height:1.2;white-space:nowrap}
.riskList{margin:0;padding-left:20px}
.riskList li{margin:10px 0}
table{width:100%;border-collapse:collapse;font-size:14px}
th,td{padding:11px 10px;border-bottom:1px solid var(--line);text-align:left}
th{font-weight:800;font-size:12px;text-transform:uppercase;letter-spacing:.04em}
details{margin:10px 0;border-radius:12px;padding:12px}
.factSection{margin-top:8px}.factRow{display:grid;grid-template-columns:minmax(0,320px) 1fr;padding:10px 8px;border-bottom:1px solid #f1f5f9;gap:12px}.factLabel{font-weight:700;color:#334155;font-size:13px}
pre{white-space:pre-wrap;background:var(--ink);color:#e2e8f0;padding:14px;border-radius:10px;max-height:280px;overflow:auto;font-size:11px}
@keyframes moveA{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.09) translate(-14px,-12px)}}
@keyframes moveB{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.06) translate(16px,14px)}}
@media (max-width:1020px){.grid3{grid-template-columns:1fr}.opt-grid{grid-template-columns:1fr}.scope-grid{grid-template-columns:1fr}}
@media (max-width:640px){.factRow{grid-template-columns:1fr}}
@media print{
  @page{size:A4 portrait;margin:12mm}
  body{background:#fff !important;color:#000 !important}
  .cover{min-height:auto !important;page-break-after:always}
  .cover::before,.cover::after,.coverShade{display:none !important}
  .coverBox{background:#fff !important;color:#000 !important;border:1px solid #e5e7eb !important;box-shadow:none !important;backdrop-filter:none !important}
  .openBtn{display:none !important}
  .book{max-width:none !important;margin:0 !important;padding:0 !important;background:#fff !important}
  .topNav,.pager,.buttons{display:none !important}
  .page,.page.active{display:block !important;animation:none !important;break-inside:avoid-page;page-break-inside:avoid;margin:0 0 10mm 0}
  .card{box-shadow:none !important;border:1px solid #d1d5db !important;break-inside:avoid-page;page-break-inside:avoid;background:#fff !important}
  details{border:1px solid #d1d5db !important;background:#fff !important}
  table{font-size:11px !important}
  th,td{padding:6px 6px !important}
  .soa-cell{text-decoration:none !important}
  .soa-cell-tip{display:none !important}
  .soa-all-strategies[open]>summary~*{display:block}
}
${SOA_THEME_CSS}
</style></head><body>
<section class="cover"><div class="coverShade"></div><div class="coverBox">
<div class="coverKicker">Statement of advice</div>
<div class="coverTitle">${escapeHtml(MODULE_SOA_TITLES[s.documentKind])}</div>
<div class="clientNames">${names}</div>
<div class="meta">
${s.adviceAttributionLine ? `<span>${escapeHtml(s.adviceAttributionLine)}</span>` : '<span>AgedCareWise Pty Ltd</span><span>AFSL 401234 (Demo)</span><span>ABN 12 345 678 901 (Demo)</span>'}
<span>Report ${escapeHtml(s.reportId)}</span><span>${formatDateOnly(s.generatedAt)}</span>
<span>Source: ${s.strategySource === 'manual_control' ? 'MC (Manual Control)' : 'SE (Suggestion Engine)'}</span>
${s.completerDisplay ? `<span>Completed by: ${escapeHtml(s.completerDisplay)}${s.completerRelationship ? ` (${escapeHtml(s.completerRelationship)})` : ''}</span>` : ''}
</div>
<button type="button" class="openBtn" onclick="openBooklet()">Begin reading your advice</button>
<p class="muted" style="margin-top:14px;font-size:13px;opacity:.85">Opens your Statement of Advice below — use <strong>Next</strong> / <strong>Previous</strong> to move through each section, or the contents list to jump to one.</p>
</div></section>
<div class="book" id="soa-document">
<div class="topNav">
  <div class="buttons">
    <button type="button" onclick="prevPage()">Previous</button>
    <button type="button" onclick="nextPage()">Next</button>
    <button type="button" data-goto="0">Contents</button>
  </div>
  <div class="navEnd"><button type="button" class="printBtn" data-print>Print or save as PDF</button><div class="pager" id="pager"></div></div>
</div>

${body}

</div>
<script src="${soaBookletScriptUrl}"></script>
</body></html>`;
}

/** Open the document in a new tab and download it, as the aged care one does. */
export function openModuleSoa(snapshot: ModuleSoaSnapshot): void {
    const html = buildModuleSoaHtml(snapshot);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const safeName = snapshot.clientName.replace(/[^a-zA-Z0-9]/g, '_');
    const label = { retirement_village: 'RetirementVillage', downsizer: 'Downsizer', granny_flat: 'GrannyFlat', cashflow_pension: 'CashflowPension' }[snapshot.documentKind];

    const tab = window.open('', '_blank');
    if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
    } else {
        const openLink = document.createElement('a');
        openLink.href = url;
        openLink.target = '_blank';
        openLink.rel = 'noopener noreferrer';
        document.body.appendChild(openLink);
        openLink.click();
        document.body.removeChild(openLink);
    }
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `Digital_SOA_${label}_${safeName}_${snapshot.generatedAt.slice(0, 10)}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/* ─── Shared plain-words builders the module files use ───────────────────────────────────────── */

/** The s947D replacement rows a plan implies, from the parts it is made of and the money it moves. */
export function replacementsFor(kinds: readonly RiskKind[], moneyFlow: readonly SoaMoneyRow[]): SoaReplacement[] {
    const rows: SoaReplacement[] = [];
    const amount = (re: RegExp): number => moneyFlow.filter((r) => re.test(r.label)).reduce((t, r) => t + r.amount, 0);
    const soldHome = kinds.includes('sellHome');
    const from = soldHome ? 'Your home, once sold' : 'Your savings and investments';
    const fromCharges = soldHome
        ? 'Agent and legal fees on the sale (included in the figures), and capital gains tax on any investments sold.'
        : 'Capital gains tax on any shares or managed funds sold to raise the money.';
    if (kinds.includes('annuityNil') || kinds.includes('annuityReturned') || kinds.includes('annuityGuaranteed')) {
        const amt = amount(/annuity/i);
        rows.push({
            from: `${from}${amt ? ` (${formatCurrency(amt)})` : ''}`,
            to: 'A lifetime annuity',
            charges: `${fromCharges} The insurer's pricing is built into the income rate; there is no separate fee.`,
            consequences: 'Centrelink counts 60% of the purchase price (30% later) and 60% of the payments as income, which usually lifts the pension.',
            lost: kinds.includes('annuityNil')
                ? 'Access to the capital, and anything for your estate from it. The income is fixed at purchase.'
                : 'Easy access to the capital. What comes back winds down over time, and the income is fixed at purchase.',
        });
    }
    if (kinds.includes('investmentProperty')) {
        const amt = amount(/property/i);
        rows.push({
            from: `${from}${amt ? ` (${formatCurrency(amt)})` : ''}`,
            to: 'An investment property',
            charges: `${fromCharges} Stamp duty, legal fees and, later, agent fees to sell.`,
            consequences: 'The property is counted as an asset and the rent as income for the Age Pension; rent is taxable.',
            lost: 'The ability to sell part of the money quickly, and the diversification of spreading it across several investments.',
        });
    }
    if (kinds.includes('shares')) {
        const amt = amount(/shares|managed/i);
        rows.push({
            from: `${from}${amt ? ` (${formatCurrency(amt)})` : ''}`,
            to: 'Shares or managed funds',
            charges: `${fromCharges} Brokerage or fund fees apply, set by the platform or fund.`,
            consequences: 'The balance is counted as an asset and income is deemed on it for the Age Pension.',
            lost: 'The certainty of a bank balance: the value moves with markets.',
        });
    }
    if (kinds.includes('superContribution') || kinds.includes('accountBasedPension')) {
        const amt = amount(/super|pension/i);
        rows.push({
            from: `${from}${amt ? ` (${formatCurrency(amt)})` : ''}`,
            to: kinds.includes('accountBasedPension') ? 'Super, then an account-based pension' : 'Super',
            charges: `${fromCharges} The fund's administration and investment fees apply, set by the fund.`,
            consequences: 'Over pension age the balance is counted and deemed; inside super, earnings are taxed concessionally or not at all.',
            lost: 'Unlimited access: minimum pension payments apply, and super rules can change.',
        });
    }
    if (kinds.includes('investmentBond')) {
        rows.push({
            from,
            to: 'An investment bond',
            charges: `${fromCharges} The bond's fees apply, set by the issuer.`,
            consequences: 'Counted as an asset and deemed for the Age Pension; earnings are taxed inside the bond at 30%.',
            lost: 'Some of the tax benefit if you withdraw within ten years.',
        });
    }
    if (kinds.includes('funeralBond')) {
        rows.push({
            from,
            to: 'A funeral bond',
            charges: `${fromCharges} The bond's fees apply, set by the issuer.`,
            consequences: 'Up to the allowable limit is exempt from the Age Pension assets test.',
            lost: 'Any use of the money other than a funeral.',
        });
    }
    return rows;
}

/** Scope lines shared by the four modules. */
export const COMMON_IN_SCOPE = [
    'How the plan you chose changes your Age Pension, your yearly cashflow and what is left to the family, measured against staying as you are.',
    'A comparison of every plan the engine modelled on your figures, using the assumptions in the appendix.',
    'Illustrative projections of pension, cashflow and net worth year by year — not forecasts of actual future balances.',
];
export const COMMON_OUT_OF_SCOPE = [
    'Choosing or endorsing a particular village, builder, lender, insurer or agent.',
    'Medical, care-needs or aged care placement decisions.',
    'Foreign assets, complex business structures, or anything not captured in your profile.',
];

/** Said only where the profile records a defined benefit pension (owner, 22 Oct 2026: it showed on every file). */
export const DEFINED_BENEFIT_SCOPE_WORDS = 'A defined benefit pension recorded in your profile is modelled as paid, assessed and taxed; the decision to take one, or to commute one, is not.';

export function hasDefinedBenefitPension(factFind: { income?: { otherIncomes?: unknown } } | null | undefined): boolean {
    const items = (factFind?.income?.otherIncomes || []) as Array<{ type?: string } | null>;
    return Array.isArray(items) && items.some((i) => isDefinedBenefitPension(i as never));
}

/** The shared out-of-scope lines for this client: the defined benefit line only where one is recorded. */
export function commonOutOfScope(factFind: { income?: { otherIncomes?: unknown } } | null | undefined): string[] {
    return hasDefinedBenefitPension(factFind) ? [...COMMON_OUT_OF_SCOPE, DEFINED_BENEFIT_SCOPE_WORDS] : [...COMMON_OUT_OF_SCOPE];
}

/* ─── Plain-words plan pieces by part, for the modules without their own piece builder ──────── */

const PIECE_WORDS: Partial<Record<RiskKind, { name: string; what: string }>> = {
    sellHome: { name: 'Selling the family home', what: 'The home is exempt from the Age Pension test; the money from selling it is not, so what you do with it next decides the pension.' },
    sellInvestmentProperty: { name: 'Selling an investment property', what: 'Capital gains tax may apply; the rent stops and the money is counted wherever it goes next.' },
    rentHome: { name: 'Keeping the home and renting it out', what: 'The rent is counted as income and is taxable; after a time the home itself can be counted as an asset.' },
    investmentProperty: { name: 'Buying an investment property', what: 'Counted as an asset, with the net rent counted as income. Stamp duty is paid on the way in.' },
    shares: { name: 'Investing in shares or managed funds', what: 'Counted as an asset; Centrelink deems a set income on the balance whether or not it is earned.' },
    investmentBond: { name: 'An investment bond', what: 'Counted and deemed like other investments; earnings are taxed inside the bond and withdrawals are tax-free after ten years.' },
    superContribution: { name: 'Putting money into super', what: 'Money goes into super as a downsizer contribution (up to $300,000 each from selling a home owned 10 years or more, from age 55) or as an after-tax contribution within the yearly limits. Earnings are taxed at no more than 15%, and nothing once it pays a pension; over pension age the balance is counted and deemed.' },
    accountBasedPension: { name: 'Starting an account-based pension', what: 'Pays you a regular income from super, with minimum amounts each year; the balance is counted and deemed.' },
    annuityNil: { name: 'A lifetime annuity (nothing back on death)', what: 'Guaranteed income for life. Centrelink counts 60% of the price as an asset and 60% of the payments as income.' },
    annuityReturned: { name: 'A lifetime annuity (capital returned)', what: 'Guaranteed income for life with a winding-down amount back on death. Counted at 60% for the assets test.' },
    annuityGuaranteed: { name: 'An annuity that returns the full amount on death', what: 'Part annuity, part insurance. The insurance part is counted at what you paid, the annuity part at 60%.' },
    funeralBond: { name: 'A funeral bond', what: 'Money set aside for a funeral. Up to the allowable limit is exempt from the assets test.' },
    gift: { name: 'A gift to family', what: 'Up to $10,000 a year ($30,000 over five years) stops being counted; anything above still counts for five years.' },
    clearDebt: { name: 'Paying off debt', what: 'Money used to clear a debt is no longer counted as an asset, and the repayments stop.' },
    fromSavings: { name: 'Paying from savings', what: 'Bank accounts and term deposits are drawn first, then investments; what is used stops being counted.' },
    villageEntry: { name: 'The village entry contribution', what: 'Paid to the operator when you move in and refunded, less the departure fee, when you leave. Not counted as an asset.' },
    grannyFlat: { name: 'The granny flat right', what: 'What you pay for the right to live there. Up to the reasonable value is exempt; above it counts as a gift.' },
    transferHome: { name: 'Transferring the home', what: 'The home becomes theirs; value given above a reasonable amount can count as a gift for five years.' },
    reverseMortgage: { name: 'A reverse mortgage', what: 'Borrowing against the home with no repayments; interest is added to the loan and it is repaid when the home is sold.' },
    heas: { name: 'The Home Equity Access Scheme', what: 'A government loan against the home, paid fortnightly, repaid with interest when the home is sold.' },
    renovate: { name: 'Spending on the home', what: 'Money moved into the home leaves the Age Pension count, because the home is exempt.' },
    stayPut: { name: 'Changing nothing', what: 'Everything stays as it is today.' },
};

/** Plan pieces for a set of parts, with amounts where the money flow names them. */
export function piecesForKinds(kinds: readonly RiskKind[], amounts: Partial<Record<RiskKind, number>> = {}): SoaPlanPiece[] {
    const seen = new Set<RiskKind>();
    return kinds
        .filter((k) => (seen.has(k) ? false : (seen.add(k), true)))
        .flatMap((k) => {
            const w = PIECE_WORDS[k];
            return w ? [{ name: w.name, what: w.what, amount: amounts[k] ?? null }] : [];
        });
}

/** "Age Pension up $4,200 a year" style reasons from the three headline figures. */
export function whyItFitsFrom(headline: { pension: number; cashflow: number; estate: number }, baseline: { name: string; pension: number; cashflow: number; estate: number }, years: number): string[] {
    const out: string[] = [];
    const say = (label: string, a: number, b: number, upIsGood: boolean, suffix: string) => {
        const d = a - b;
        if (Math.abs(d) < 1) { out.push(`${label} is unchanged${suffix}.`); return; }
        const better = upIsGood ? d > 0 : d < 0;
        out.push(`${label} is ${better ? 'better' : 'worse'} by ${formatCurrency(Math.abs(d))}${suffix} than ${baseline.name.toLowerCase()}.`);
    };
    say('The Age Pension', headline.pension, baseline.pension, true, ' a year');
    say('Cashflow', headline.cashflow, baseline.cashflow, true, ' a year');
    say(`The estate after ${years} years`, headline.estate, baseline.estate, true, '');
    if (headline.cashflow < 0) out.push(`Cashflow is still short by ${formatCurrency(Math.abs(headline.cashflow))} a year, which the projections draw from savings.`);
    return out;
}
